import { FC } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { ThemeFormValues } from '../types'
import {
  AddBtn,
  AddButtonWrapper,
  AnswersFieldGroup,
  AnswersTitle,
  BlockContainer,
  ErrorText,
  FieldGroup,
  FieldLabel,
  OptionInput,
  OptionRow,
  OptionsHeader,
  OptionsHeaderCell,
  OptionsListWrapper,
  RadioCol,
  RemoveBtn,
  SectionTitle,
  StyledTextarea,
} from './styled'

const EMPTY_OPTION = { text: '', isCorrect: false }
const REQUIRED_FIELD_MESSAGE = 'Это обязательное поле'
const MIN_OPTIONS_MESSAGE = 'Заполните минимум 2 варианта ответа'
const OPTIONS_FIELD_NAME = 'controlQuestion.options'
const MIN_FILLED_OPTIONS = 2

const ControlQuestionBlock: FC = () => {
  const {
    register,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<ThemeFormValues>()

  const { fields, append, remove } = useFieldArray({
    control,
    name: OPTIONS_FIELD_NAME,
  })

  const options = watch(OPTIONS_FIELD_NAME) ?? []

  const handleCorrectChange = (selectedIndex: number) => {
    options.forEach((_, i) => {
      setValue(
        `controlQuestion.options.${i}.isCorrect`,
        i === selectedIndex,
        { shouldValidate: false }
      )
    })
  }

  const questionError = errors?.controlQuestion?.question?.message as
    | string
    | undefined
  const optionsError = (errors?.controlQuestion?.options as { message?: string } | undefined)
    ?.message
  const optionErrors = errors?.controlQuestion?.options as
    | { text?: { message?: string } }[]
    | undefined

  const getRequiredEmptyOptionIndexes = () => {
    const currentOptions = watch(OPTIONS_FIELD_NAME) ?? []
    const filledOptionsCount = currentOptions.filter((option) =>
      option.text.trim()
    ).length
    const requiredEmptyCount = Math.max(MIN_FILLED_OPTIONS - filledOptionsCount, 0)

    if (requiredEmptyCount === 0) {
      return []
    }

    return currentOptions
      .map((option, index) => ({ index, isEmpty: !option.text.trim() }))
      .filter((option) => option.isEmpty)
      .slice(0, requiredEmptyCount)
      .map((option) => option.index)
  }

  const shouldShowOptionError = (index: number) =>
    getRequiredEmptyOptionIndexes().includes(index)

  const validateOptionText = (index: number) => {
    return shouldShowOptionError(index) ? MIN_OPTIONS_MESSAGE : true
  }

  return (
    <BlockContainer>
      <SectionTitle>Контрольный вопрос</SectionTitle>

      <FieldGroup>
        <FieldLabel>Вопрос</FieldLabel>
        <StyledTextarea
          {...register('controlQuestion.question', {
            required: REQUIRED_FIELD_MESSAGE,
            validate: (value) => !!value.trim() || REQUIRED_FIELD_MESSAGE,
          })}
          placeholder="Вопрос"
          className={questionError ? 'has-error' : ''}
        />
        {questionError && <ErrorText>{questionError}</ErrorText>}
      </FieldGroup>

      <AnswersFieldGroup>
        <AnswersTitle>Варианты ответов</AnswersTitle>
        <OptionsListWrapper>
          <OptionsHeader>
            <OptionsHeaderCell>Верный</OptionsHeaderCell>
            <OptionsHeaderCell>Вариант ответа</OptionsHeaderCell>
            <OptionsHeaderCell />
          </OptionsHeader>
          {fields.map((field, index) => (
            <OptionRow key={field.id}>
              <RadioCol>
                <input
                  type="radio"
                  name="controlQuestion-correct"
                  checked={!!options[index]?.isCorrect}
                  onChange={() => handleCorrectChange(index)}
                />
              </RadioCol>
              <OptionInput
                {...register(`controlQuestion.options.${index}.text`, {
                  validate: () => validateOptionText(index),
                })}
                type="text"
                placeholder="Вариант ответа"
                className={
                  (optionsError || optionErrors?.[index]?.text?.message) &&
                  shouldShowOptionError(index)
                    ? 'has-error'
                    : ''
                }
              />
              <RemoveBtn type="button" onClick={() => remove(index)} title="Удалить">
                <DeleteOutlined aria-hidden="true" />
                Удалить
              </RemoveBtn>
            </OptionRow>
          ))}
        </OptionsListWrapper>
        {optionsError && <ErrorText>{optionsError}</ErrorText>}
        <AddButtonWrapper>
          <AddBtn
            type="button"
            onClick={() => append(EMPTY_OPTION)}
          >
            <PlusOutlined /> Добавить
          </AddBtn>
        </AddButtonWrapper>
      </AnswersFieldGroup>
    </BlockContainer>
  )
}

export default ControlQuestionBlock
